import React, { useMemo, useState } from "react";

const faqData = [
  {
    category: "General",
    question: "What is Vortex Network?",
    answer:
      "Vortex Network is a gaming-focused network providing Minecraft Bedrock addons, projects, community features, and other gaming-related content."
  },
  {
    category: "General",
    question: "Where can I find your latest updates?",
    answer:
      "You can find the latest Vortex Network updates, addon releases, announcements, and project news on the website and Discord server."
  },
  {
    category: "Minecraft",
    question: "What Minecraft versions do your addons support?",
    answer:
      "Our addons are developed for Minecraft Bedrock Edition. Supported versions can vary between releases, so always check the addon page for the latest compatibility information."
  },
  {
    category: "Minecraft",
    question: "How do I install a Vortex Network addon?",
    answer:
      "Download the addon from its official download page, open the Minecraft addon file, and Minecraft should import it automatically. After importing, activate the required Behaviour Packs and Resource Packs in your world."
  },
  {
    category: "Minecraft",
    question: "Why isn't my addon working?",
    answer:
      "First make sure you are using a supported Minecraft Bedrock version. Also check that the required Behaviour Pack and Resource Pack are enabled. If the problem continues, check the addon documentation or report the issue to the Vortex Network team."
  },
  {
    category: "Minecraft",
    question: "Can I use the addons on my server?",
    answer:
      "Yes, our addons can be used on compatible Minecraft Bedrock worlds and servers. Make sure the server supports the Minecraft version required by the addon."
  },
  {
    category: "Prison Addon",
    question: "What is the Prison Addon?",
    answer:
      "The Prison Addon is a Minecraft Bedrock prison experience featuring custom mines, pickaxes, economy and progression systems, enchantments, crates, keys, tokens, and other prison gameplay features."
  },
  {
    category: "Prison Addon",
    question: "How do I report a Prison Addon bug?",
    answer:
      "Please report bugs through the official Vortex Network community channels. Include your Minecraft version, addon version, what you were doing when the problem happened, and any error messages you received."
  },
  {
    category: "Skyblock Addon",
    question: "What is the Skyblock Addon?",
    answer:
      "The Skyblock Addon provides a custom Skyblock experience for Minecraft Bedrock Edition with island systems, progression, economy features, GUIs, mobs, and other Skyblock mechanics."
  },
  {
    category: "Downloads",
    question: "Where can I download your addons?",
    answer:
      "Official addon downloads are available through the addon pages on the Vortex Network website and supported distribution platforms."
  },
  {
    category: "Downloads",
    question: "Are Vortex Network addons free?",
    answer:
      "Our publicly released addons are generally available to download for free. Always use the official Vortex Network download pages to make sure you are downloading the correct version."
  },
  {
    category: "Support",
    question: "How can I get help?",
    answer:
      "If you need help with an addon or another Vortex Network project, visit the support section or join the official Discord community."
  },
  {
    category: "Support",
    question: "How do I suggest a new feature?",
    answer:
      "Feature suggestions can be submitted through the Vortex Network community. Please describe the feature clearly and explain how you think it would improve the project."
  }
];

function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  const categories = useMemo(() => {
    return ["All", ...new Set(faqData.map((faq) => faq.category))];
  }, []);

  const filteredFAQs = useMemo(() => {
    return faqData.filter((faq) => {
      const matchesCategory =
        activeCategory === "All" || faq.category === activeCategory;

      const searchText = search.toLowerCase();

      const matchesSearch =
        faq.question.toLowerCase().includes(searchText) ||
        faq.answer.toLowerCase().includes(searchText) ||
        faq.category.toLowerCase().includes(searchText);

      return matchesCategory && matchesSearch;
    });
  }, [search, activeCategory]);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="faq-page">
      <style>{`
        .faq-page {
          min-height: 100vh;
          background:
            radial-gradient(
              circle at top right,
              rgba(0, 170, 255, 0.10),
              transparent 35%
            ),
            radial-gradient(
              circle at top left,
              rgba(120, 70, 255, 0.08),
              transparent 30%
            ),
            #0b0f14;
          color: #ffffff;
          padding: 80px 20px;
          font-family: Arial, Helvetica, sans-serif;
        }

        .faq-container {
          width: 100%;
          max-width: 1100px;
          margin: 0 auto;
        }

        .faq-header {
          text-align: center;
          margin-bottom: 45px;
        }

        .faq-badge {
          display: inline-block;
          padding: 8px 15px;
          border: 1px solid rgba(0, 170, 255, 0.3);
          background: rgba(0, 170, 255, 0.08);
          border-radius: 30px;
          color: #43c7ff;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 1px;
          margin-bottom: 18px;
        }

        .faq-header h1 {
          font-size: clamp(38px, 6vw, 64px);
          margin: 0 0 15px;
          font-weight: 800;
          letter-spacing: -2px;
        }

        .faq-header h1 span {
          color: #32bfff;
        }

        .faq-header p {
          max-width: 700px;
          margin: 0 auto;
          color: #9da7b3;
          line-height: 1.7;
          font-size: 16px;
        }

        .faq-search {
          position: relative;
          margin: 0 auto 30px;
          max-width: 750px;
        }

        .faq-search input {
          width: 100%;
          box-sizing: border-box;
          padding: 18px 22px 18px 52px;
          border-radius: 14px;
          border: 1px solid #202a35;
          outline: none;
          background: #11171e;
          color: white;
          font-size: 15px;
          transition: 0.2s ease;
        }

        .faq-search input:focus {
          border-color: #32bfff;
          box-shadow: 0 0 0 3px rgba(50, 191, 255, 0.08);
        }

        .faq-search-icon {
          position: absolute;
          left: 20px;
          top: 50%;
          transform: translateY(-50%);
          color: #71808f;
          font-size: 19px;
        }

        .faq-categories {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          justify-content: center;
          margin-bottom: 35px;
        }

        .faq-category {
          border: 1px solid #202a35;
          background: #11171e;
          color: #9da7b3;
          padding: 10px 16px;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 14px;
        }

        .faq-category:hover {
          border-color: #32bfff;
          color: white;
        }

        .faq-category.active {
          background: #32bfff;
          color: #061018;
          border-color: #32bfff;
          font-weight: 700;
        }

        .faq-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .faq-item {
          background: rgba(17, 23, 30, 0.9);
          border: 1px solid #202a35;
          border-radius: 14px;
          overflow: hidden;
          transition: border-color 0.2s ease,
                      transform 0.2s ease;
        }

        .faq-item:hover {
          border-color: #30404f;
        }

        .faq-item.open {
          border-color: rgba(50, 191, 255, 0.45);
        }

        .faq-question {
          width: 100%;
          border: none;
          background: transparent;
          color: white;
          padding: 21px 22px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 20px;
          text-align: left;
          cursor: pointer;
          font-size: 16px;
          font-weight: 600;
        }

        .faq-question-left {
          display: flex;
          align-items: center;
          gap: 13px;
        }

        .faq-number {
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          border-radius: 8px;
          background: rgba(50, 191, 255, 0.08);
          color: #32bfff;
          font-size: 12px;
          font-weight: 700;
        }

        .faq-arrow {
          width: 30px;
          height: 30px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #19212a;
          color: #9da7b3;
          transition: transform 0.25s ease;
          flex-shrink: 0;
        }

        .faq-item.open .faq-arrow {
          transform: rotate(180deg);
          color: #32bfff;
        }

        .faq-answer {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s ease;
        }

        .faq-item.open .faq-answer {
          max-height: 400px;
        }

        .faq-answer-content {
          padding: 0 22px 22px 65px;
          color: #9da7b3;
          line-height: 1.7;
          font-size: 14px;
        }

        .faq-category-label {
          display: inline-block;
          margin-bottom: 8px;
          color: #32bfff;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 1px;
          font-weight: 700;
        }

        .faq-empty {
          text-align: center;
          padding: 60px 20px;
          background: #11171e;
          border: 1px solid #202a35;
          border-radius: 14px;
          color: #9da7b3;
        }

        .faq-empty h3 {
          color: white;
          margin-top: 0;
          margin-bottom: 8px;
        }

        .faq-support {
          margin-top: 45px;
          padding: 35px;
          text-align: center;
          border-radius: 18px;
          border: 1px solid #202a35;
          background:
            linear-gradient(
              135deg,
              rgba(50, 191, 255, 0.08),
              rgba(120, 70, 255, 0.06)
            );
        }

        .faq-support h2 {
          margin: 0 0 10px;
          font-size: 25px;
        }

        .faq-support p {
          color: #9da7b3;
          margin: 0 0 22px;
          line-height: 1.6;
        }

        .faq-support-button {
          display: inline-block;
          padding: 12px 20px;
          border-radius: 10px;
          background: #32bfff;
          color: #061018;
          text-decoration: none;
          font-weight: 700;
          font-size: 14px;
          transition: transform 0.2s ease;
        }

        .faq-support-button:hover {
          transform: translateY(-2px);
        }

        @media (max-width: 600px) {
          .faq-page {
            padding: 50px 15px;
          }

          .faq-header h1 {
            letter-spacing: -1px;
          }

          .faq-question {
            padding: 18px 15px;
          }

          .faq-answer-content {
            padding: 0 15px 20px 58px;
          }

          .faq-support {
            padding: 25px 18px;
          }
        }
      `}</style>

      <div className="faq-container">
        <header className="faq-header">
          <div className="faq-badge">VORTEX NETWORK SUPPORT</div>

          <h1>
            Frequently Asked <span>Questions</span>
          </h1>

          <p>
            Find answers to some of the most common questions about Vortex
            Network, our Minecraft addons, downloads, and projects.
          </p>
        </header>

        <div className="faq-search">
          <span className="faq-search-icon">⌕</span>

          <input
            type="text"
            placeholder="Search frequently asked questions..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setOpenIndex(null);
            }}
          />
        </div>

        <div className="faq-categories">
          {categories.map((category) => (
            <button
              key={category}
              className={`faq-category ${
                activeCategory === category ? "active" : ""
              }`}
              onClick={() => {
                setActiveCategory(category);
                setOpenIndex(null);
              }}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="faq-list">
          {filteredFAQs.length > 0 ? (
            filteredFAQs.map((faq, index) => {
              const isOpen = openIndex === index;

              return (
                <div
                  className={`faq-item ${isOpen ? "open" : ""}`}
                  key={`${faq.category}-${faq.question}`}
                >
                  <button
                    className="faq-question"
                    onClick={() => toggleFAQ(index)}
                    aria-expanded={isOpen}
                  >
                    <span className="faq-question-left">
                      <span className="faq-number">
                        {String(index + 1).padStart(2, "0")}
                      </span>

                      <span>{faq.question}</span>
                    </span>

                    <span className="faq-arrow">⌄</span>
                  </button>

                  <div className="faq-answer">
                    <div className="faq-answer-content">
                      <div className="faq-category-label">
                        {faq.category}
                      </div>

                      <div>{faq.answer}</div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="faq-empty">
              <h3>No FAQs found</h3>
              <p>
                Try searching for something else or select a different
                category.
              </p>
            </div>
          )}
        </div>

        <div className="faq-support">
          <h2>Still need help?</h2>

          <p>
            Can't find the answer you're looking for? Get in touch with the
            Vortex Network community for help and support.
          </p>

          <a
            className="faq-support-button"
            href="https://discord.com/invite/D8Z4HyRT6t"
            target="_blank"
            rel="noopener noreferrer"
          >
            Join Our Discord
          </a>
        </div>
      </div>
    </div>
  );
}

export default FAQ;